/**
 * @file components/workbench/AdminWorkbench.tsx
 * @description Admin 角色工作台（完整版，使用設計 tokens + shell 組件）。
 *
 * 信息架構（從上到下）：
 *   1. PageShell header — eyebrow + 大標問候 + meta
 *   2. 4 張 StatCard — 待處理案件 / 進行中稽核 / 本月已完成 / 到期警示
 *   3. 業務漏斗 — 6 階段全流程在途量 + 平均停留天數
 *   4. 我的待辦 + 異常預警（左右雙欄）
 *   5. 業務分布（驗證方案 + 風險等級）
 *   6. 稽核員負載 + 近期動態
 */

import { Button, Empty, Progress, Tooltip } from 'antd';
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { formatLongDate } from '@/lib/date-time';
import { useAuthStore } from '@/lib/auth-context';
import { useAdminDashboard } from '@/hooks/useWorkbench';
import { PageShell, PageMetaItem, SectionCard, StatCard, StatusPill } from '@/components/shell';
import { color, font, leading, radius, space, text, tracking, weight } from '@/styles/tokens';
import type {
  FunnelStageData,
  DashboardTodoItem,
  DashboardAlertItem,
  AuditorLoadItem,
  DashboardActivityItem,
} from '@zhcb/shared';
import {
  AlarmClock,
  Activity,
  ArrowRight,
  Building2,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  FileBarChart,
  FileSignature,
  Inbox,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  TriangleAlert,
  Users,
} from 'lucide-react';

// ─── 前端本地常量 ─────────────────────────────────────────────────────────────

/**
 * 業務漏斗各阶段的 accent 顏色映射（纯前端显示逻辑，不随接口变化）。
 */
const FUNNEL_ACCENT: Record<string, string> = {
  intake: color.indigo[500],
  assignment: color.indigo[600],
  audit: color.violet[500],
  report: color.violet[600],
  decision: color.sky[600],
  issued: color.emerald[600],
};

/** 默认 accent 颜色（未知 key 时回退） */
const FUNNEL_DEFAULT_ACCENT = color.indigo[400];

/**
 * 業務漏斗阶段 key → 中文顯示名稱映射（純前端顯示邏輯，不隨接口變化）。
 */
const STAGE_LABELS: Record<string, string> = {
  intake: '受理',
  assignment: '派案',
  audit: '稽核中',
  report: '報告審查',
  decision: '驗證決定',
  issued: '已出證',
};

/**
 * 驗證方案代码 → 顯示名稱映射。
 */
const SCHEME_LABEL_MAP: Record<string, string> = {
  TOP: '有機農產品',
  TAP: '產銷履歷 TAP',
  ECO: '友善環境',
};

/**
 * 驗證方案代码 → 顏色映射。
 */
const SCHEME_COLOR_MAP: Record<string, string> = {
  TOP: color.emerald[600],
  TAP: color.indigo[600],
  ECO: color.sky[600],
};

/** 默認方案顏色 */
const SCHEME_DEFAULT_COLOR = color.amber[600];

/**
 * 風險等級標籤 → tone 映射（用于 DistributionBar）。
 */
const RISK_TONE_MAP: Record<string, string> = {
  低風險: 'success',
  中風險: 'warning',
  高風險: 'danger',
  未評估: 'muted',
};

// ─── 子組件 ────────────────────────────────────────────────────────────────────

/**
 * 業務漏斗：橫向流程圖 + 在途量 + 平均停留天數。
 * 接受 stages prop，accent 顏色由本地映射常量决定。
 */
function BusinessFunnel({ stages }: { stages: FunnelStageData[] }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${stages.length || 1}, minmax(0, 1fr))`,
        gap: 0,
        position: 'relative',
      }}
    >
      {stages.map((stage, idx) => {
        const isLast = idx === stages.length - 1;
        const accent = FUNNEL_ACCENT[stage.key] ?? FUNNEL_DEFAULT_ACCENT;
        return (
          <div
            key={stage.key}
            style={{
              padding: `${space[4]}px ${space[3]}px`,
              borderRight: isLast ? 'none' : `1px solid ${color.border.subtle}`,
              position: 'relative',
            }}
          >
            {/* 階段編號 + 名稱 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: space[2],
                marginBottom: space[3],
              }}
            >
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: radius.full,
                  background: accent,
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: weight.bold,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: font.mono,
                  flexShrink: 0,
                }}
              >
                {idx + 1}
              </span>
              <span
                style={{
                  fontSize: text.sm,
                  fontWeight: weight.semibold,
                  color: color.text.secondary,
                  letterSpacing: tracking.wide,
                }}
              >
                {STAGE_LABELS[stage.key] ?? stage.key}
              </span>
            </div>

            {/* 在途量 */}
            <div
              style={{
                fontFamily: font.display,
                fontSize: text['3xl'],
                fontWeight: weight.bold,
                color: color.text.primary,
                lineHeight: leading.tight,
                letterSpacing: tracking.tight,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {stage.count}
            </div>

            {/* 平均停留 */}
            <div style={{ marginTop: space[2], fontSize: text.sm, color: color.text.tertiary }}>
              {stage.key === 'issued' ? (
                <>本月新增</>
              ) : (
                <>
                  平均停留{' '}
                  <span style={{ color: color.text.primary, fontWeight: weight.semibold }}>
                    {stage.avgDays}
                  </span>{' '}
                  日
                </>
              )}
            </div>

            {/* 連接箭頭（除最後一個） */}
            {!isLast && (
              <div
                style={{
                  position: 'absolute',
                  right: -8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 16,
                  height: 16,
                  borderRadius: radius.full,
                  background: color.surface.base,
                  border: `1px solid ${color.border.subtle}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                }}
              >
                <ArrowRight size={10} color={color.text.muted} strokeWidth={2.4} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** 已讀待辦 id 的 localStorage key（序116：待辦已讀/未讀標記，前端本機持久化） */
const TODO_READ_STORAGE_KEY = 'zhcb-workbench-todo-read';

/** 讀取已讀待辦 id 集合（解析失敗回空集合） */
function loadReadTodoIds(): Set<string> {
  try {
    const raw = localStorage.getItem(TODO_READ_STORAGE_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

/** 持久化已讀待辦 id 集合（僅保留最近 200 筆防膨脹） */
function persistReadTodoIds(ids: Set<string>) {
  try {
    localStorage.setItem(TODO_READ_STORAGE_KEY, JSON.stringify([...ids].slice(-200)));
  } catch {
    /* 隱私模式等寫入失敗時靜默忽略：已讀標記屬增強體驗，非關鍵數據 */
  }
}

/**
 * 待辦項列（序116：標記已讀/未讀 + 狀態 + 計劃編號 + 驗證方案 + 時間期限）。
 */
function TodoRow({
  item,
  read,
  onClick,
}: {
  item: DashboardTodoItem;
  read: boolean;
  onClick: () => void;
}) {
  const priorityTone =
    item.priority === 'urgent' ? 'danger' : item.priority === 'high' ? 'warning' : 'info';
  const priorityLabel =
    item.priority === 'urgent' ? '緊急' : item.priority === 'high' ? '高' : '中';

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        all: 'unset',
        boxSizing: 'border-box',
        display: 'block',
        width: '100%',
        padding: `${space[3]}px ${space[5]}px`,
        cursor: 'pointer',
        transition: 'background 120ms cubic-bezier(0.16, 1, 0.3, 1)',
        borderBottom: `1px solid ${color.border.subtle}`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = color.surface.muted;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: space[3] }}>
        {/* 序116：已讀/未讀標記 */}
        <StatusPill tone={read ? 'muted' : 'brand'} size="sm" dot>
          {read ? '已讀' : '未讀'}
        </StatusPill>
        <StatusPill tone={priorityTone} size="sm" dot>
          {priorityLabel}
        </StatusPill>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: text.md,
              fontWeight: read ? weight.medium : weight.semibold,
              color: read ? color.text.secondary : color.text.primary,
              marginBottom: 3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.title}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: space[3],
              fontSize: text.sm,
              color: color.text.tertiary,
              flexWrap: 'wrap',
            }}
          >
            {/* 序116：計劃編號優先展示（未派案回落業務單號） */}
            <span style={{ fontFamily: font.mono, color: color.text.secondary }}>
              {item.planCode ?? item.code}
            </span>
            {item.status && (
              <>
                <span style={{ color: color.border.strong }}>·</span>
                <span>{item.status}</span>
              </>
            )}
            {item.scheme && (
              <>
                <span style={{ color: color.border.strong }}>·</span>
                <span>{item.scheme}</span>
              </>
            )}
          </div>
        </div>
        <div
          style={{
            fontSize: text.sm,
            color: item.priority === 'urgent' ? color.state.danger : color.text.tertiary,
            fontWeight: item.priority === 'urgent' ? weight.semibold : weight.medium,
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <AlarmClock size={13} />
          {item.dueIn}
        </div>
      </div>
    </button>
  );
}

/**
 * 異常預警列。
 */
function AlertRow({ alert }: { alert: DashboardAlertItem }) {
  const palette: Record<string, { bg: string; fg: string; icon: typeof TriangleAlert }> = {
    danger: { bg: color.state.dangerSoft, fg: color.state.danger, icon: TriangleAlert },
    warning: { bg: color.state.warningSoft, fg: color.state.warning, icon: AlarmClock },
    info: { bg: color.state.infoSoft, fg: color.state.info, icon: Activity },
  };
  const p = palette[alert.severity] ?? palette.info!;
  const Icon = p.icon;

  return (
    <div
      style={{
        display: 'flex',
        gap: space[3],
        padding: `${space[3]}px ${space[5]}px`,
        borderBottom: `1px solid ${color.border.subtle}`,
        alignItems: 'flex-start',
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: radius.md,
          background: p.bg,
          color: p.fg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginTop: 2,
        }}
      >
        <Icon size={16} strokeWidth={2.2} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: text.md,
            fontWeight: weight.semibold,
            color: color.text.primary,
            marginBottom: 2,
            lineHeight: leading.snug,
          }}
        >
          {alert.title}
        </div>
        <div style={{ fontSize: text.sm, color: color.text.tertiary, lineHeight: leading.snug }}>
          {alert.description}
        </div>
      </div>
    </div>
  );
}

/**
 * 分布橫條列表。
 */
function DistributionBar({
  items,
  total,
}: {
  items: Array<{ label: string; count: number; color?: string; tone?: string }>;
  total: number;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space[3] }}>
      {items.map((item) => {
        const pct = total > 0 ? (item.count / total) * 100 : 0;
        const barColor =
          item.color ??
          (item.tone === 'success'
            ? color.state.success
            : item.tone === 'warning'
              ? color.state.warning
              : item.tone === 'danger'
                ? color.state.danger
                : color.text.muted);
        return (
          <div key={item.label}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 6,
              }}
            >
              <span
                style={{
                  fontSize: text.md,
                  color: color.text.secondary,
                  fontWeight: weight.medium,
                }}
              >
                {item.label}
              </span>
              <span
                style={{
                  fontFamily: font.display,
                  fontSize: text.md,
                  fontWeight: weight.semibold,
                  color: color.text.primary,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {item.count}
                <span
                  style={{
                    fontSize: text.sm,
                    color: color.text.muted,
                    fontWeight: weight.regular,
                    marginLeft: 6,
                  }}
                >
                  {pct.toFixed(0)}%
                </span>
              </span>
            </div>
            <div
              style={{
                height: 6,
                background: color.surface.sunken,
                borderRadius: radius.full,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${pct}%`,
                  background: barColor,
                  borderRadius: radius.full,
                  transition: 'width 480ms cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * 稽核員負載行。
 */
function AuditorLoadRow({ auditor }: { auditor: AuditorLoadItem }) {
  const ratio = auditor.cases / auditor.capacity;
  const tone = ratio >= 0.9 ? 'danger' : ratio >= 0.7 ? 'warning' : 'success';
  const barColor =
    tone === 'danger'
      ? color.state.danger
      : tone === 'warning'
        ? color.state.warning
        : color.state.success;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: space[3],
        padding: `${space[3]}px ${space[5]}px`,
        borderBottom: `1px solid ${color.border.subtle}`,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: radius.full,
          background: color.brand.primarySoft,
          color: color.brand.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: text.base,
          fontWeight: weight.bold,
          flexShrink: 0,
        }}
      >
        {auditor.name.charAt(0)}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: 4,
          }}
        >
          <span
            style={{ fontSize: text.md, fontWeight: weight.semibold, color: color.text.primary }}
          >
            {auditor.name}
            <span
              style={{
                fontSize: text.sm,
                color: color.text.muted,
                fontWeight: weight.medium,
                marginLeft: 6,
              }}
            >
              {auditor.role}
            </span>
          </span>
          <span
            style={{
              fontFamily: font.mono,
              fontSize: text.sm,
              color: color.text.secondary,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {auditor.cases}/{auditor.capacity}
          </span>
        </div>
        <div
          style={{
            height: 4,
            background: color.surface.sunken,
            borderRadius: radius.full,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${(auditor.cases / auditor.capacity) * 100}%`,
              background: barColor,
              borderRadius: radius.full,
              transition: 'width 480ms cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * 近期動態行。
 * DashboardActivityItem 不含 icon/tone，固定使用 Activity 圖標與 info 配色。
 */
function ActivityRow({ event }: { event: DashboardActivityItem }) {
  const p = { bg: color.state.infoSoft, fg: color.state.info };

  return (
    <div
      style={{
        display: 'flex',
        gap: space[3],
        padding: `${space[3]}px ${space[5]}px`,
        borderBottom: `1px solid ${color.border.subtle}`,
        alignItems: 'flex-start',
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: radius.full,
          background: p.bg,
          color: p.fg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Activity size={14} />
      </div>
      <div style={{ minWidth: 0, flex: 1, paddingTop: 2 }}>
        <div style={{ fontSize: text.md, color: color.text.primary, lineHeight: leading.snug }}>
          <span style={{ fontWeight: weight.semibold }}>{event.actor}</span>
          <span style={{ color: color.text.tertiary, margin: '0 4px' }}>{event.action}</span>
          <span style={{ fontWeight: weight.medium }}>{event.target}</span>
        </div>
        <div style={{ fontSize: text.sm, color: color.text.muted, marginTop: 2 }}>{event.time}</div>
      </div>
    </div>
  );
}

// ─── 主組件 ────────────────────────────────────────────────────────────────────

/**
 * Admin 工作台組件。
 * 顯示業務漏斗、待辦、異常預警、分布圖、稽核員負載、近期動態。
 * 數據來源：GET /api/dashboard/admin（useAdminDashboard hook）。
 */
export function AdminWorkbench() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user!);

  /* 從 API 獲取 dashboard 聚合數據；採用 0 值渲染策略，無需顯式 loading 狀態 */
  const { data: d } = useAdminDashboard();

  /* 序116：待辦已讀/未讀標記（本機持久化，惰性初始化自 localStorage） */
  const [readTodoIds, setReadTodoIds] = useState<Set<string>>(() => loadReadTodoIds());

  /* 問候語：依時段切換 */
  const hour = new Date().getHours();
  const greeting = hour < 6 ? '深夜好' : hour < 12 ? '早安' : hour < 18 ? '午安' : '晚安';

  /* 本月完成度計算（來自真實數據） */
  const monthlyCompleted = d?.stats.monthlyCompleted ?? 0;
  const monthlyTarget = d?.stats.monthlyTarget ?? 60;
  const completionPct = Math.round((monthlyCompleted / monthlyTarget) * 100);

  /* 驗證方案分布：將 DistributionItem 轉為 DistributionBar 所需格式 */
  const schemeItems = (d?.schemeBreakdown ?? []).map((item) => ({
    label: SCHEME_LABEL_MAP[item.label] ?? item.label,
    count: item.count,
    color: SCHEME_COLOR_MAP[item.label] ?? SCHEME_DEFAULT_COLOR,
  }));
  const totalSchemes = schemeItems.reduce((sum, s) => sum + s.count, 0);

  /* 風險等級分布：將 DistributionItem 轉為含 tone 格式 */
  const riskItems = (d?.riskBreakdown ?? []).map((item) => ({
    label: item.label,
    count: item.count,
    tone: RISK_TONE_MAP[item.label] ?? 'muted',
  }));
  const totalRisk = riskItems.reduce((sum, r) => sum + r.count, 0);

  return (
    <PageShell
      title={
        <span>
          {greeting}，{user.name}
        </span>
      }
      description={
        <>
          今日有 <strong style={{ color: color.text.primary }}>{d?.todos.length ?? 0}</strong>{' '}
          個事項需要您決定。
        </>
      }
      meta={
        <>
          <PageMetaItem
            icon={<CalendarDays size={13} />}
            label="日期"
            value={formatLongDate(new Date())}
          />
          <PageMetaItem
            icon={<Building2 size={13} />}
            label="獲證組織"
            value={`${d?.certifiedOrgs ?? 0} 家`}
          />
          <PageMetaItem
            icon={<Activity size={13} />}
            label="本期在途"
            value={`${d?.inProgressTotal ?? 0} 件`}
          />
        </>
      }
      actions={
        <>
          <Button
            icon={<FileBarChart size={14} />}
            onClick={() => void navigate({ to: '/analytics' })}
          >
            查看月報
          </Button>
          <Button
            type="primary"
            icon={<Sparkles size={14} />}
            onClick={() => void navigate({ to: '/bulletin' })}
          >
            新建公告
          </Button>
        </>
      }
    >
      {/* ═══ 第一排：4 張統計卡 ═══ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: space[4],
          marginBottom: space[6],
        }}
      >
        <StatCard
          title="待處理案件"
          value={d?.stats.pendingCases ?? 0}
          suffix="件"
          tone="brand"
          icon={<Inbox size={20} />}
          onClick={() => void navigate({ to: '/dispatch' })}
        />
        <StatCard
          title="進行中稽核"
          value={d?.stats.activeAudits ?? 0}
          suffix="件"
          tone="info"
          icon={<ClipboardCheck size={20} />}
        />
        <StatCard
          title="本月已完成"
          value={monthlyCompleted}
          suffix="件"
          tone="success"
          icon={<CheckCircle2 size={20} />}
          deltaLabel={`目標 ${monthlyTarget} 件`}
          footer={
            <Progress
              percent={completionPct}
              showInfo={false}
              strokeColor={color.state.success}
              size={['100%', 4]}
            />
          }
        />
        <StatCard
          title="到期警示"
          value={d?.stats.expiringSoon ?? 0}
          suffix="件"
          tone="warning"
          icon={<AlarmClock size={20} />}
          deltaLabel="30 日內到期"
        />
      </div>

      {/* ═══ 業務漏斗 ═══ */}
      <div style={{ marginBottom: space[6] }}>
        <SectionCard
          icon={<TrendingUp size={14} />}
          title="業務漏斗"
          description="申請受理至證書發出全流程在途量"
          actions={
            <Button type="text" size="small" icon={<ArrowRight size={13} />}>
              查看詳情
            </Button>
          }
          padless
        >
          {(d?.funnel ?? []).length === 0 ? (
            <div style={{ padding: `${space[6]}px` }}>
              <Empty description="暫無資料" />
            </div>
          ) : (
            <BusinessFunnel stages={d?.funnel ?? []} />
          )}
        </SectionCard>
      </div>

      {/* ═══ 我的待辦 + 異常預警（左右兩欄） ═══ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
          gap: space[4],
          marginBottom: space[6],
        }}
      >
        <SectionCard
          icon={<FileSignature size={14} />}
          title="我的待辦事項"
          description="需要您親自決定的事項"
          actions={
            <Tooltip title="查看全部待辦">
              <Button type="text" size="small">
                全部 ({d?.todos.length ?? 0})
              </Button>
            </Tooltip>
          }
          padless
        >
          {(d?.todos ?? []).length === 0 ? (
            <div style={{ padding: `${space[6]}px` }}>
              <Empty description="暫無待辦事項" />
            </div>
          ) : (
            (d?.todos ?? []).map((item) => (
              <TodoRow
                key={item.id}
                item={item}
                read={readTodoIds.has(item.id)}
                onClick={() => {
                  // 序116：點擊即標記已讀（本機持久化）
                  setReadTodoIds((prev) => {
                    const next = new Set(prev);
                    next.add(item.id);
                    persistReadTodoIds(next);
                    return next;
                  });
                  // 依待辦類型直接關聯到對應詳情：案件 → 案件詳情；驗證決定 → 決定詳情；
                  // 皆無時回落到派案列表（防禦性，正常後端已帶 caseId/decisionId）。
                  if (item.decisionId != null) {
                    void navigate({
                      to: '/decisions/$id',
                      params: { id: String(item.decisionId) },
                    });
                  } else if (item.caseId != null) {
                    void navigate({
                      to: '/dispatch/$caseId',
                      params: { caseId: String(item.caseId) },
                    });
                  } else {
                    void navigate({ to: '/dispatch' });
                  }
                }}
              />
            ))
          )}
        </SectionCard>

        <SectionCard
          icon={<TriangleAlert size={14} />}
          title="異常預警"
          description="按嚴重度排序"
          padless
        >
          {(d?.alerts ?? []).length === 0 ? (
            <div style={{ padding: `${space[6]}px` }}>
              <Empty description="暫無異常預警" />
            </div>
          ) : (
            (d?.alerts ?? []).map((alert) => <AlertRow key={alert.id} alert={alert} />)
          )}
        </SectionCard>
      </div>

      {/* ═══ 業務分布（驗證方案 + 風險等級） ═══ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: space[4],
          marginBottom: space[6],
        }}
      >
        <SectionCard
          icon={<ShieldCheck size={14} />}
          title="驗證方案分布"
          description={`累計 ${totalSchemes} 家獲證組織`}
        >
          {schemeItems.length === 0 ? (
            <Empty description="暫無資料" />
          ) : (
            <DistributionBar items={schemeItems} total={totalSchemes} />
          )}
        </SectionCard>

        <SectionCard
          icon={<TriangleAlert size={14} />}
          title="風險等級分布"
          description="基於最近一次風險評估"
        >
          {riskItems.length === 0 ? (
            <Empty description="暫無資料" />
          ) : (
            <DistributionBar items={riskItems} total={totalRisk} />
          )}
        </SectionCard>
      </div>

      {/* ═══ 稽核員負載 + 近期動態 ═══ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: space[4],
        }}
      >
        <SectionCard
          icon={<Users size={14} />}
          title="稽核員負載"
          description="當前在手案件數 / 容量上限"
          padless
        >
          {(d?.auditorLoad ?? []).length === 0 ? (
            <div style={{ padding: `${space[6]}px` }}>
              <Empty description="暫無負載資料" />
            </div>
          ) : (
            (d?.auditorLoad ?? []).map((auditor) => (
              <AuditorLoadRow key={auditor.id} auditor={auditor} />
            ))
          )}
        </SectionCard>

        <SectionCard
          icon={<Activity size={14} />}
          title="近期動態"
          description="最新業務動態"
          padless
        >
          {(d?.recentActivity ?? []).length === 0 ? (
            <div style={{ padding: `${space[6]}px` }}>
              <Empty description="暫無近期動態" />
            </div>
          ) : (
            (d?.recentActivity ?? []).map((event) => <ActivityRow key={event.id} event={event} />)
          )}
        </SectionCard>
      </div>
    </PageShell>
  );
}
